import { Button, ButtonGroup, LoadingSpinner, Label, Icon, Tooltip, Input } from '@opengov/component-library/capital';
import React, { useState, useRef } from 'react';
import { useFocusElement } from '../../../hooks/useFocusElement';
import { AiPromptMessages } from '../../../types';
import { Modal } from '../Modal';
import styles from './AiPromptModal.scss';
import { AiApi, AiGeneratedSummaryLength } from '../../../api/ai-api';
import { removeHtmlTags } from '../../../stories/utils/editor';
import { extractErrorMsg } from '../../../stories/utils/errors';
import { useToast } from '../../../stories/components/Toast/context/useToast.context';

type Props = {
  messages: AiPromptMessages;
  value: string;
  entityId: number;
  closeModal(): void;
  setValue(value: string): void;
};

export function AiPromptModal({ messages, value, entityId, closeModal, setValue }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  useFocusElement(inputRef);
  const { toggleToast } = useToast();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [prompt, setPrompt] = React.useState('');
  const [summary, setSummary] = useState<string>('');
  const [aiResponse, setAiResponse] = useState<string>('');

  const [contentLength, setContentLength] = useState<AiGeneratedSummaryLength>(AiGeneratedSummaryLength.SHORT);

  const insertAfterGenerateSummary = () => {
    setValue(summary);
    closeModal();
  };

  const handleSubmit = async (userPrompt) => {
    try {
      setIsLoading(true);
      setSummary('');
      setAiResponse('');
      const response = await AiApi.submitQuestion({ entityId, userPrompt, contentLength, verbose: true });
      const newContent = value.length > 0 ? `${value}<br>${response}` : response;
      setSummary(newContent);
      setAiResponse(response);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      toggleToast({
        title: messages.somethingWentWrongError,
        body: extractErrorMsg(error, messages.unableToGenerateAnswer),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title={messages.askAiToWrite} onClose={closeModal} dataTest="ai-prompt-modal">
      <div className={styles.container}>
        <p className={styles.label}>{messages.askAiDescription}</p>
        <Input.Text
          value={prompt}
          onChange={(value) => setPrompt(value)}
          placeholder={messages.tellOgAssist}
          data-test="prompt-input"
          className={styles.paddingBottom}
          ref={inputRef}
        />
        <Label>{messages.contentLength}</Label>
        <ButtonGroup variant="neutral" data-test="view-button-group">
          <Button
            selected={contentLength === AiGeneratedSummaryLength.SHORT}
            onClick={() => setContentLength(AiGeneratedSummaryLength.SHORT)}
            disabled={isLoading}
          >
            {messages.short}
          </Button>
          <Button
            selected={contentLength === AiGeneratedSummaryLength.MEDIUM}
            onClick={() => setContentLength(AiGeneratedSummaryLength.MEDIUM)}
            disabled={isLoading}
          >
            {messages.medium}
          </Button>
          <Button
            selected={contentLength === AiGeneratedSummaryLength.LONG}
            onClick={() => setContentLength(AiGeneratedSummaryLength.LONG)}
            disabled={isLoading}
          >
            {messages.long}
          </Button>
        </ButtonGroup>
        {isLoading ? (
          <LoadingSpinner
            className={styles.paddingTop}
            dataTest="ai-prompt-loader"
            message={messages.generatingLoader}
          />
        ) : (
          <div className={styles.paddingTop}>
            {summary && (
              <>
                <textarea
                  className={styles.textArea}
                  disabled={true}
                  placeholder="..."
                  value={removeHtmlTags(aiResponse)}
                />
                <Tooltip className={styles.tooltip} content={messages.info} placement="top-end">
                  <div className={styles.learn}>
                    <Icon className={styles.icon} icon="utility-info" />
                    <div>Learn how this works</div>
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        )}
        <div
          className={
            (!isLoading && summary.length === 0) || isLoading
              ? styles.buttonsContainerDisabled
              : styles.buttonsContainer
          }
        >
          {summary.length > 0 && (
            <Button variant="naked" onClick={() => handleSubmit(prompt)} dataTest="regenerate-submit-ai-prompt">
              <div className={styles.regenerate}>
                <Icon icon="action-refresh" />
                <div>Regenerate</div>
              </div>
            </Button>
          )}
          <div className={styles.buttons}>
            <Button variant="naked" onClick={() => closeModal()} dataTest="cancel-ai-prompt">
              {messages.cancel}
            </Button>
            {isLoading || prompt.length === 0 ? (
              <Button variant="primary" disabled data-test="submit-ai-prompt">
                {messages.generate}
              </Button>
            ) : summary ? (
              <Button
                variant="primary"
                onClick={() => insertAfterGenerateSummary()}
                dataTest="insert-generated-ai-answer"
              >
                {messages.insert}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => handleSubmit(prompt)} dataTest="submit-ai-prompt">
                {messages.generate}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
